<template>
  <el-dialog
    :visible="visible"
    :title="dialogTitle"
    width="720px"
    class="capital-org-detail-dialog"
    @opened="afterOpen"
    @close="onClose"
  >
    <div class="scroll-wrap">
      <el-form
        ref="form"
        :model="orgForm"
        label-position="top"
        :rules="rules"
      >
        <!-- 基本信息 -->
        <div class="section-title">基本信息</div>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="机构名称" prop="orgName">
              <el-input
                v-model="orgForm.orgName"
                :disabled="isViewMode"
                placeholder="请输入机构名称"
                size="small"
                maxlength="50"
                show-word-limit
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="投资IRR" prop="investIrr">
              <div class="input-with-suffix">
                <el-input-number
                  v-model="orgForm.investIrr"
                  :disabled="isViewMode"
                  :controls="false"
                  :min="0"
                  :max="100"
                  :precision="0"
                  placeholder="请输入"
                  size="small"
                />
                <span class="suffix">%</span>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="融资比例" prop="investRate">
              <div class="input-with-suffix">
                <el-input-number
                  v-model="orgForm.investRate"
                  :disabled="isViewMode"
                  :controls="false"
                  :min="0"
                  :max="100"
                  :precision="0"
                  placeholder="请输入"
                  size="small"
                />
                <span class="suffix">%</span>
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider class="divider-dashed"></el-divider>

        <!-- 推单控制 -->
        <div class="section-title">推单控制</div>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="单日推单上限" prop="dailyMaxPushOrderNum">
              <div class="input-with-suffix">
                <el-input-number
                  v-model="orgForm.dailyMaxPushOrderNum"
                  :disabled="isViewMode"
                  :controls="false"
                  :min="0"
                  :precision="0"
                  placeholder="请输入"
                  size="small"
                />
                <span class="suffix">单</span>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="推单比重" prop="pushOrderRate">
              <div class="input-with-suffix">
                <el-input-number
                  v-model="orgForm.pushOrderRate"
                  :disabled="isViewMode"
                  :controls="false"
                  :min="0"
                  :max="100"
                  :precision="0"
                  placeholder="请输入"
                  size="small"
                />
                <span class="suffix">%</span>
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider class="divider-dashed"></el-divider>

        <!-- 清分方式 -->
        <div class="section-title">清分方式</div>
        <el-form-item label="" prop="royaltyMethod">
          <el-radio-group
            v-model="orgForm.royaltyMethod"
            :disabled="isViewMode"
            class="royalty-radio-group"
          >
            <div
              v-for="item in royaltyMethodOptions"
              :key="item.value"
              class="royalty-card"
              :class="{ 'is-active': orgForm.royaltyMethod === item.value }"
              @click="!isViewMode && (orgForm.royaltyMethod = item.value)"
            >
              <el-radio :label="item.value" class="royalty-radio">{{ item.name }}</el-radio>
              <div class="royalty-tip">{{ item.tip }}</div>
            </div>
          </el-radio-group>
        </el-form-item>
      </el-form>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button size="small" @click="visible = false">取 消</el-button>
      <el-button
        v-if="!isViewMode"
        size="small"
        type="primary"
        @click="onSubmit"
      >确 定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { getCapitalOrgDetail, editCapitalOrg } from "./api.js";
import { RoyaltyMethodList } from "@/enum/dict";

// 清分方式选项（带提示）
const ROYALTY_METHOD_OPTIONS = [
  {
    value: 1,
    name: "差额回款",
    tip: "全额清分给资方，资方扣除应得后差额返还",
  },
];

export default {
  name: "CapitalOrgDetail",
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    id: {
      type: [Number, String],
      required: false,
      default: null,
    },
    mode: {
      type: String,
      required: true,
      validator: (value) => ["edit", "view"].includes(value),
    },
  },
  data() {
    return {
      royaltyMethodOptions: ROYALTY_METHOD_OPTIONS,
      orgForm: {
        id: null,
        orgName: "",
        royaltyMethod: null,
        dailyMaxPushOrderNum: null,
        pushOrderRate: null,
        investRate: null,
        investIrr: null,
      },
      rules: {
        orgName: [
          { required: true, message: "机构名称不能为空", trigger: "blur" },
        ],
        investIrr: [
          {
            type: "number",
            required: true,
            message: "投资IRR不能为空",
            trigger: "blur",
          },
        ],
        investRate: [
          {
            type: "number",
            required: true,
            message: "融资比例不能为空",
            trigger: "blur",
          },
        ],
        dailyMaxPushOrderNum: [
          {
            type: "number",
            required: true,
            message: "单日推单上限不能为空",
            trigger: "blur",
          },
        ],
        pushOrderRate: [
          {
            type: "number",
            required: true,
            message: "推单比重不能为空",
            trigger: "blur",
          },
        ],
        royaltyMethod: [
          {
            type: "number",
            required: true,
            message: "清分方式不能为空",
            trigger: "change",
          },
        ],
      },
    };
  },
  computed: {
    dialogTitle() {
      return this.mode === "view" ? "机构资方详情" : "编辑机构资方";
    },
    isViewMode() {
      return this.mode === "view";
    },
  },
  methods: {
    async afterOpen() {
      if (!this.id) return;
      const res = await getCapitalOrgDetail({ id: this.id });
      this.orgForm = {
        id: res.id,
        orgName: res.orgName || "",
        royaltyMethod: res.royaltyMethod ?? null,
        dailyMaxPushOrderNum: res.dailyMaxPushOrderNum ?? null,
        pushOrderRate: res.pushOrderRate ?? null,
        investRate: res.investRate ?? null,
        investIrr: res.investIrr ?? null,
      };
    },
    onClose() {
      this.$emit("update:visible", false);
    },
    async onSubmit() {
      const valid = await this.$refs.form.validate();
      if (!valid) return;
      await editCapitalOrg({ ...this.orgForm });
      this.$message.success("编辑成功");
      this.onClose();
      this.$emit("refreshList");
    },
  },
};
</script>

<style lang="scss" scoped>
.capital-org-detail-dialog {
  /deep/ .el-form-item__label {
    font-weight: 600;
    padding-bottom: 8px;
  }
}

.scroll-wrap {
  max-height: 65vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 20px 20px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
  padding-left: 10px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 14px;
    background-color: #409eff;
  }
}

.input-with-suffix {
  display: flex;
  align-items: center;
  width: 100%;

  .el-input-number {
    flex: 1;
    min-width: 0;
    width: auto;
  }

  .suffix {
    flex: 0 0 auto;
    margin-left: 8px;
    color: #909399;
    font-size: 13px;
    white-space: nowrap;
  }
}

.royalty-radio-group {
  display: flex;
  gap: 16px;
}

.royalty-card {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #409eff;
  }

  &.is-active {
    border-color: #409eff;
    background-color: #ecf5ff;
  }

  .royalty-radio {
    display: block;
    margin-bottom: 8px;

    /deep/ .el-radio__label {
      font-weight: 500;
    }
  }

  .royalty-tip {
    font-size: 12px;
    color: #909399;
    line-height: 1.5;
  }

  &.is-active .royalty-tip {
    color: #606266;
  }
}

/deep/ .el-divider--horizontal {
    display: block;
    height: 1px;
    width: 100%;
    margin: 12px 0;
}

/deep/ .divider-dashed.el-divider--horizontal {
  height: 0 !important;
  background-color: transparent !important;
  border-top: 1px dashed #dcdfe6 !important;
}
</style>
